import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  Exam,
  ExamAttendee,
  ExamAttendeeStats,
  ExamAttemptDetailResDto,
  ExamDetail,
  ExamFilters,
  ExamResDto,
  CreateExamDto,
  GetExamAttendeesParams,
  ExamAttendeeStatusLabel,
} from "../types/exam-management.types";

const ADMIN_EXAMS_ENDPOINT = "/api/admin/exams";

export const examManagementApi = {
  /**
   * GET /api/admin/exams - List exams (backend response shape as-is)
   */
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    filters?: ExamFilters;
  }): Promise<{ data: Exam[]; total: number }> => {
    const filters = params?.filters;

    const apiParams: Record<string, unknown> = {
      page: params?.page ?? 1,
      limit: params?.pageSize ?? 10,
    };

    if (filters?.status !== undefined) apiParams.status = filters.status;
    if (filters?.search?.trim()) apiParams.search = filters.search.trim();
    if (filters?.sortBy) apiParams.sortBy = filters.sortBy;
    if (filters?.sortOrder) apiParams.sortOrder = filters.sortOrder;
    if (filters?.createdAtStart) apiParams.createdAtStart = filters.createdAtStart;
    if (filters?.createdAtEnd) apiParams.createdAtEnd = filters.createdAtEnd;
    if (filters?.educationIds?.length) apiParams.educationIds = filters.educationIds;
    if (filters?.minPassingScore !== undefined) apiParams.minPassingScore = filters.minPassingScore;
    if (filters?.maxPassingScore !== undefined) apiParams.maxPassingScore = filters.maxPassingScore;
    if (filters?.minSuccessRate !== undefined) apiParams.minSuccessRate = filters.minSuccessRate;
    if (filters?.maxSuccessRate !== undefined) apiParams.maxSuccessRate = filters.maxSuccessRate;

    const { data: responseData } = await apiClient.get(
      ADMIN_EXAMS_ENDPOINT,
      { params: apiParams }
    );

    const items: Exam[] = Array.isArray(responseData?.data) ? responseData.data : [];
    const total: number = responseData?.count ?? 0;

    return { data: items, total };
  },

  getRaw: async (id: string): Promise<{ data: ExamResDto }> => {
    const { data: responseData } = await apiClient.get(
      `${ADMIN_EXAMS_ENDPOINT}/${id}`
    );
    const item: ExamResDto = responseData?.data ?? responseData;
    if (!item) throw new Error("Exam not found");
    return { data: item };
  },

  getById: async (id: string): Promise<Exam> => {
    const { data } = await examManagementApi.getRaw(id);
    return data;
  },

  getDetailById: async (id: string): Promise<ExamDetail> => {
    const { data } = await examManagementApi.getRaw(id);
    return data;
  },

  /**
   * Create exam - POST /api/admin/exams
   */
  create: async (payload: CreateExamDto) => {
    const { data: responseData } = await apiClient.post(ADMIN_EXAMS_ENDPOINT, payload);
    const data = responseData?.data ?? responseData;
    return { data };
  },

  /**
   * PATCH /api/admin/exams/{id}
   * Sınavı güncelle (arşivleme vb.).
   */
  update: async (id: string, payload: Record<string, unknown>) => {
    const { data: responseData } = await apiClient.patch(
      `${ADMIN_EXAMS_ENDPOINT}/${id}`,
      payload
    );
    return responseData?.data ?? responseData;
  },

  /**
   * DELETE /api/admin/exams/{id}
   * Sınavı sil.
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_EXAMS_ENDPOINT}/${id}`);
  },

  /**
   * PATCH /api/admin/exams/{id}/questions/reorder
   * Sınav sorularını sırala. orderedIds tüm aktif soru ID'lerini
   * içermek zorunda — partial reorder desteklenmiyor.
   */
  reorderQuestions: async (
    examId: string,
    orderedIds: string[],
  ): Promise<void> => {
    await apiClient.patch(
      `${ADMIN_EXAMS_ENDPOINT}/${examId}/questions/reorder`,
      { orderedIds },
    );
  },

  /**
   * GET /api/admin/exams/{examId}/attempts
   * Sınav katılımcılarını ve istatistikleri getir.
   */
  getAttendees: async (
    examId: string,
    params?: GetExamAttendeesParams,
  ): Promise<{ data: ExamAttendee[]; total: number; stats: ExamAttendeeStats }> => {
    type AttemptItem = {
      id?: string | null;
      userID?: string;
      educationID?: string | null;
      educationName?: string | null;
      user?: {
        firstName?: string;
        lastName?: string;
        title?: string;
        department?: { id?: string; name?: string };
      };
      startedAt?: string;
      finishedAt?: string | null;
      score?: number;
      status?: number; // 0=NOT_PARTICIPATED, 1=PASSED, 2=FAILED
      createdAt?: string;
      updatedAt?: string;
    };

    const apiParams: Record<string, unknown> = {};
    if (params?.search?.trim()) apiParams.search = params.search.trim();
    if (params?.status !== undefined) apiParams.status = params.status;
    if (params?.departmentIds?.length) apiParams.departmentIds = params.departmentIds;
    if (params?.sortBy) apiParams.sortBy = params.sortBy;
    if (params?.sortOrder) apiParams.sortOrder = params.sortOrder;

    const { data: rawResponse } = await apiClient.get(
      `${ADMIN_EXAMS_ENDPOINT}/${examId}/attempts`,
      { params: Object.keys(apiParams).length > 0 ? apiParams : undefined },
    );

    // Backend response yapısı:
    // { success, status, data: [...items], count: N, message, successRate?, averageCompletionTime?, notParticipatedCount? }
    // VEYA nested: { success, status, data: { items: [...], count, successRate, ... } }
    const dataField = rawResponse?.data ?? rawResponse;
    let items: AttemptItem[];
    let count: number;
    let successRate: number;
    let averageCompletionTime: number;
    let notParticipatedCount: number;

    if (Array.isArray(dataField)) {
      // Flat yapı: data direkt items dizisi, count root seviyede
      items = dataField;
      count = rawResponse?.count ?? items.length;
      successRate = rawResponse?.successRate ?? 0;
      averageCompletionTime = rawResponse?.averageCompletionTime ?? 0;
      notParticipatedCount = rawResponse?.notParticipatedCount ?? 0;
    } else if (dataField && typeof dataField === "object") {
      // Nested yapı: data = { items, count, successRate, ... }
      items = Array.isArray(dataField.items) ? dataField.items : [];
      count = dataField.count ?? items.length;
      successRate = dataField.successRate ?? 0;
      averageCompletionTime = dataField.averageCompletionTime ?? 0;
      notParticipatedCount = dataField.notParticipatedCount ?? 0;
    } else {
      items = [];
      count = 0;
      successRate = 0;
      averageCompletionTime = 0;
      notParticipatedCount = 0;
    }

    const stats: ExamAttendeeStats = {
      count,
      successRate,
      averageCompletionTime,
      notParticipatedCount,
    };
    
    function mapAttemptStatus(s?: number, startedAt?: string): ExamAttendeeStatusLabel {
      if(s === 0 && startedAt) return "basladi";
      switch (s) {
        case 1: return "gecti";
        case 2: return "kaldi";
        default: return "katilmadi";
      }
    }

    const mapped: ExamAttendee[] = items.map((a, i) => {
      const name =
        a.user?.firstName && a.user?.lastName
          ? `${a.user.firstName} ${a.user.lastName}`
          : i18n.t("exams.participantFallback");

      return {
        id: a.id ?? `attendee-${a.userID ?? "u"}-${a.educationID ?? i + 1}`,
        name,
        title: a.user?.title ?? "-",
        department: a.user?.department?.name ?? "-",
        educationID: a.educationID ?? null,
        educationName: a.educationName ?? "-",
        score: a.status === 0 ? null : (a.score ?? 0),
        status: mapAttemptStatus(a.status, a.startedAt),
        rawStatus: a.status ?? 0,
      };
    });

    return { data: mapped, total: stats.count, stats };
  },

  /**
   * GET /api/admin/exams/exam-attempts/{attemptId}/answers
   * Sınav denemesi cevaplarını getir.
   */
  getAttemptAnswers: async (
    attemptId: string,
  ): Promise<ExamAttemptDetailResDto> => {
    const { data } = await apiClient.get<{ data: ExamAttemptDetailResDto }>(
      `${ADMIN_EXAMS_ENDPOINT}/exam-attempts/${attemptId}/answers`,
    );
    if (!data?.data) {
      throw new Error("Attempt answers not found");
    }
    return data.data;
  },
};
